<template>
  <el-dialog
    :model-value="modelValue"
    title="选择图文消息"
    width="900px"
    append-to-body
    @update:model-value="emit('update:modelValue', $event)"
  >
    <p class="font-medium text-slate-700 mb-3">物料配置</p>

    <!-- 按钮区：查看模式 / 编辑模式 -->
    <div class="flex mb-2">
      <template v-if="!isEditing">
        <el-button type="primary" @click="onAdd">添加图文</el-button>
        <el-button type="primary" plain @click="onBatchEdit">批量编辑</el-button>
        <el-button type="danger" :disabled="selectedRows.length === 0" @click="onDeleteSaved">
          删除
        </el-button>
      </template>
      <template v-else>
        <el-button type="primary" plain @click="addRow">增加多条</el-button>
        <el-button type="danger" :disabled="selectedRows.length === 0" @click="onDeleteRow">
          删除
        </el-button>
        <el-button type="primary" :loading="saveLoading" @click="onSave"> 保存 </el-button>
      </template>
    </div>

    <DataTableWithPagination
      v-model:page="page"
      v-model:page-size="pageSize"
      :data="tableRows"
      :loading="loading"
      :total="total"
      :show-column-settings="false"
      highlight-current-row
      padding="0"
      @page-change="loadList"
      @size-change="loadList"
      @selection-change="(rows: WxArticleRow[]) => (selectedRows = rows)"
      @current-change="
        (row: WxArticleRow | null) => {
          if (!isEditing) selectedArticle = row;
        }
      "
    >
      <el-table-column type="selection" width="44" />
      <el-table-column label="图文标题">
        <template #default="{ row }">
          <el-input v-if="isRowEditable(row)" v-model="row.title" placeholder="图文标题" />
          <span v-else>{{ row.title }}</span>
        </template>
      </el-table-column>
      <el-table-column label="图文描述">
        <template #default="{ row }">
          <el-input v-if="isRowEditable(row)" v-model="row.description" placeholder="图文描述" />
          <span v-else>{{ row.description }}</span>
        </template>
      </el-table-column>
      <el-table-column label="图文链接">
        <template #default="{ row }">
          <el-input v-if="isRowEditable(row)" v-model="row.link_url" placeholder="图文链接" />
          <span v-else>{{ row.link_url }}</span>
        </template>
      </el-table-column>
      <el-table-column label="图文封面" width="80">
        <template #default="{ row }">
          <el-upload
            v-if="isRowEditable(row)"
            :show-file-list="false"
            :auto-upload="false"
            accept="image/*"
            @change="(f) => f.raw && onImageChange(row, f.raw)"
          >
            <el-avatar shape="square" :size="32" :src="row.cover_url || undefined">
              <el-icon><Plus /></el-icon>
            </el-avatar>
          </el-upload>
          <el-avatar v-else-if="row.cover_url" shape="square" :size="32" :src="row.cover_url" />
          <el-avatar v-else shape="square" :size="32">
            <el-icon><Picture /></el-icon>
          </el-avatar>
        </template>
      </el-table-column>
    </DataTableWithPagination>

    <template #footer>
      <el-button @click="onCancel">取消</el-button>
      <el-button v-if="!isEditing" type="primary" :disabled="!confirmTarget" @click="onConfirm">
        确认
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { Picture, Plus } from '@element-plus/icons-vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { getUploadUrl } from '@/api/common';
import { toBearerHeader } from '@/utils/token';
import DataTableWithPagination from '@/components/DataTableWithPagination.vue';
import type { WxArticleRow } from '@/types/chatBox';

const props = defineProps<{
  modelValue: boolean;
  accountId?: string;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', val: boolean): void;
  (e: 'confirm', article: Record<string, unknown>): void;
}>();

const loading = ref(false);
const saveLoading = ref(false);
const isEditing = ref(false);
/** 是否批量编辑模式（true 时已保存的行也可编辑；false 时仅新增行可编辑） */
const batchMode = ref(false);
const tableRows = ref<WxArticleRow[]>([]);

function isRowEditable(row: WxArticleRow) {
  return isEditing.value && (batchMode.value || !!row.isNew);
}
const selectedRows = ref<WxArticleRow[]>([]);
const selectedArticle = ref<WxArticleRow | null>(null);

/**
 * 待发送的图文：优先取点击行，其次取勾选。
 * 勾选框本用于批量编辑/删除，但用户看到勾选框就会用它选发送对象，
 * 只认点击行会让「明明勾上了却不能确认」；勾多行时对象不唯一，仍视为未选。
 */
const confirmTarget = computed<WxArticleRow | null>(
  () =>
    selectedArticle.value ??
    (selectedRows.value.length === 1 ? (selectedRows.value[0] ?? null) : null),
);
const total = ref(0);
const page = ref(1);
const pageSize = ref(10);

const uploadUrl = getUploadUrl();
const authToken = toBearerHeader(localStorage.getItem('token'));

watch(
  () => props.modelValue,
  (val) => {
    if (val) {
      isEditing.value = false;
      batchMode.value = false;
      selectedArticle.value = null;
      selectedRows.value = [];
      page.value = 1;
      loadList();
    }
  },
);

async function loadList() {
  loading.value = true;
  try {
    const { channelMaterialsApi, parseMaterialContent } =
      await import('@/api/smartService/channelMaterials');
    const { list, total: t } = await channelMaterialsApi
      .list({
        account_id: props.accountId || '',
        type: 5,
        page: page.value,
        per_page: pageSize.value,
      })
      .catch(() => ({ list: [], total: 0 }));
    tableRows.value = (list as Record<string, unknown>[]).map((r) => {
      const c = parseMaterialContent(r.content);
      return {
        id: r.id as number | undefined,
        title: (c.title as string) || '',
        description: (c.description as string) || '',
        link_url: (c.url as string) || '',
        cover_url: (c.image_url as string) || '',
      };
    });
    total.value = t;
  } finally {
    loading.value = false;
  }
}

function emptyRow(): WxArticleRow {
  return { title: '', description: '', link_url: '', cover_url: '', isNew: true };
}

function onAdd() {
  isEditing.value = true;
  selectedRows.value = [];
  tableRows.value.push(emptyRow());
}

function onBatchEdit() {
  isEditing.value = true;
  selectedRows.value = [];
}

function addRow() {
  tableRows.value.push(emptyRow());
}

function onDeleteRow() {
  tableRows.value = tableRows.value.filter((r) => !selectedRows.value.includes(r));
  selectedRows.value = [];
}

async function onDeleteSaved() {
  if (selectedRows.value.length === 0) return;
  await ElMessageBox.confirm('确定删除选中的图文消息吗？', '提示', { type: 'warning' });
  const ids = selectedRows.value.map((r) => r.id).filter((v): v is number => typeof v === 'number');
  const { channelMaterialsApi } = await import('@/api/smartService/channelMaterials');
  await Promise.all(ids.map((id) => channelMaterialsApi.delete(id)));
  ElMessage.success('删除成功');
  loadList();
}

async function onImageChange(row: WxArticleRow, file: File) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(uploadUrl, {
    method: 'POST',
    headers: { Authorization: authToken || '' },
    body: form,
  });
  const data = (await res.json()) as { code?: number; data?: { url?: string } };
  if (data?.code === 0 && data?.data?.url) {
    row.cover_url = data.data.url;
  }
}

async function onSave() {
  const rows = tableRows.value.filter((r) => r.title.trim() || r.link_url.trim());
  if (rows.length === 0) {
    ElMessage.warning('请至少填写一条数据');
    return;
  }
  saveLoading.value = true;
  try {
    const { channelMaterialsApi } = await import('@/api/smartService/channelMaterials');
    await Promise.all(
      rows.map((row) => {
        const content = {
          title: row.title,
          description: row.description,
          url: row.link_url,
          image_url: row.cover_url,
        };
        if (row.id) {
          return channelMaterialsApi.update(row.id, { content });
        }
        return channelMaterialsApi.create({
          account_id: props.accountId || '',
          name: row.title || '图文消息',
          type: 5,
          content,
        });
      }),
    );
    ElMessage.success('保存成功');
    isEditing.value = false;
    selectedRows.value = [];
    loadList();
  } finally {
    saveLoading.value = false;
  }
}

function onCancel() {
  if (isEditing.value) {
    isEditing.value = false;
    loadList();
  } else {
    emit('update:modelValue', false);
  }
}

function onConfirm() {
  if (!confirmTarget.value) return;
  emit('confirm', confirmTarget.value as unknown as Record<string, unknown>);
  emit('update:modelValue', false);
}
</script>
